import { Box, Loader, Text } from '@ui-lib';
import type { PropsWithChildren, ReactNode } from 'react';

import * as styles from './Menu.css';

type MenuItemCommonProps =
  | {
      selected?: boolean;
      label: string;
      /** Use this to render an icon inside the tab adjacent to the tab label */

      icon?: React.ReactElement;
      /** Used this to position the icon before or after tab label */
      iconPosition?: 'left' | 'right';
      disabled?: boolean;
      loading?: boolean;
    }
  | {
      selected?: boolean;
      label?: string;
      children: ReactNode;
      /** Use this to render an icon inside the tab adjacent to the tab label */

      icon?: React.ReactElement;
      /** Used this to position the icon before or after tab label */
      iconPosition?: 'left' | 'right';
      disabled?: boolean;
      loading?: boolean;
    };

type MenuItemButtonProps = MenuItemCommonProps & {
  onClick: () => void;
  href?: never;
  newTab?: never;
};

type MenuItemLinkProps = MenuItemCommonProps & {
  href: string;
  target?: string;
  newTab?: boolean;
  onClick?: never;
};

function spacesToHyphens(str: string): string {
  return str.replace(/\s+/g, '-').toLowerCase();
}

export const MenuItem = ({
  ref,
  children,
  selected = false,
  label,
  iconPosition = 'left',
  icon,
  disabled,
  loading,
  ...rest
}: PropsWithChildren<MenuItemLinkProps | MenuItemButtonProps> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <Box
      ref={ref}
      component={rest.onClick || disabled ? 'button' : 'a'}
      onClick={!disabled ? rest.onClick : undefined}
      href={rest.href}
      target={rest.newTab ? '_blank' : undefined}
      rel={rest.newTab ? 'noopener noreferrer' : undefined}
      disabled={disabled}
      className={styles.menuItem({
        iconPosition,
        selected,
      })}
      data-testid={`${spacesToHyphens(label ?? 'a')}-menu-item`}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {icon}
          <Text
            className={styles.menuLabel({
              disabled,
            })}
            size="small"
            weight="demibold"
          >
            {label}
            {children}
          </Text>
        </>
      )}
    </Box>
  );
};

MenuItem.displayName = 'MenuItem';
